/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import Checkmark from '@react/react-spectrum/Icon/Checkmark';
import Link from '@react/react-spectrum/Link';
import {Menu, MenuItem} from '@react/react-spectrum/Menu';
import React, {Component} from 'react';
import {FormattedMessage} from 'react-intl';
import getConfig from '../../../config';
import {handleLanguageChange} from '../../../utils/Locale';
import './LanguageSwitcher.scss';
import LanguageSwitcherDropdown from './LanguageSwitcherDropdown';

type Props = {
	currentLocale: string;
};

const languages = [
	'cs',
	'da',
	'de',
	'en',
	'es',
	'fr',
	'it',
	'nl',
	'nb',
	'pl',
	'pt',
	'fi',
	'sv',
	'tr',
	'ru',
	'zh-Hans',
	'zh-Hant',
	'ja',
	'ko',
];

class LangugageSwitcher extends Component<Props> {
	handleLanguageSelect(val: string) {
		/* istanbul ignore else */
		if (val.match(/^[A-z0-9-]+$/)) {
			handleLanguageChange(val);
		}
	}

	renderLanguageList() {
		const supportedLangs = getConfig('SUPPORTED_LANGUAGES');
		const languageOptions = languages.map((langOption) => {
			/* istanbul ignore else */
			if (supportedLangs[langOption]) {
				if (this.props.currentLocale === langOption) {
					return (
						<MenuItem value={langOption} key={langOption} className='selected lang-item'>
							{supportedLangs[langOption].displayName}
							<Checkmark size='S' />
						</MenuItem>
					);
				}
				return (
					<MenuItem value={langOption} key={langOption} className='lang-item'>
						{supportedLangs[langOption].displayName}
					</MenuItem>
				);
			}

			return null;
		});
		return languageOptions;
	}

	render() {
		const supportedLangs = getConfig('SUPPORTED_LANGUAGES');
		const currentLocale = this.props.currentLocale || 'en';

		return (
			<div className='language-container'>
				<label htmlFor='pl-languageSelectEl--input' id='language-picker-title'>
					<FormattedMessage id='app.footer.language' />
				</label>
				&nbsp;
				{/* @ts-ignore */}
				<LanguageSwitcherDropdown
					trigger='click'
					placement='top'
					flip={false}
					crossOffset={70}
					className='dropdown'
					onSelect={event => this.handleLanguageSelect(event)}
				>
					<Link aria-labelledby='language-picker-title' tabIndex={0}>
						{supportedLangs[currentLocale].displayName}
					</Link>
					<Menu role='menu' className='language-list'>
						{this.renderLanguageList()}
					</Menu>
				</LanguageSwitcherDropdown>
			</div>
		);
	}
}

export default LangugageSwitcher;
